import { alpha, styled } from '@mui/material/styles';
import type { CardProps } from '@mui/material';
import { Card, Tooltip, Typography } from '@mui/material';

import type { ReactNode } from 'react';

import { fShortenNumber } from '../../../utils/formatNumber';
import Iconify from '../../../components/iconify';

const StyledIcon = styled('div')(({ theme }) => ({
    margin: 'auto',
    display: 'flex',
    borderRadius: '50%',
    alignItems: 'center',
    width: theme.spacing(8),
    height: theme.spacing(8),
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
}));

/** Palette entries that carry the lighter/darker shades this card reads. */
export type AppWidgetSummaryColor = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

export type AppWidgetSummaryProps = {
    title: string;
    total: number;
    tooltip?: ReactNode;
    icon: string;
    color?: AppWidgetSummaryColor;
    sx?: CardProps['sx'];
};

export default function AppWidgetSummary(
    { title, total, tooltip, icon, color = 'primary', sx, ...other }: AppWidgetSummaryProps) {

    return (
        <Card
            sx={ {
                py: 5,
                boxShadow: 0,
                textAlign: 'center',
                color: (theme) => theme.palette[color].darker,
                bgcolor: (theme) => theme.palette[color].lighter,
                ...sx,
            } }
            { ...other }
        >
            <StyledIcon
                sx={ {
                    color: (theme) => theme.palette[color].dark,
                    backgroundImage: (theme) =>
                        `linear-gradient(135deg, ${ alpha(theme.palette[color].dark, 0) } 0%, ${ alpha(
                            theme.palette[color].dark,
                            0.24
                        ) } 100%)`,
                } }
            >
                <Iconify icon={ icon } width={ 24 }/>
            </StyledIcon>

            <Typography variant="h3">{ fShortenNumber(total) || '0' }</Typography>

            <Tooltip title={ tooltip }>
                <Typography variant="subtitle2" sx={ { opacity: 0.72 } }>
                    { title }
                </Typography>
            </Tooltip>
        </Card>
    );
}
